import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";

const STEPS = ["Applicant", "Project", "Funding"];

export default function Grant() {
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [stories, setStories] = useState([]);
  useEffect(() => {
    api.get("/grant-stories").then((r) => setStories(r.data)).catch(() => {});
  }, []);
  const [form, setForm] = useState({
    applicant_name: "",
    email: "",
    phone: "",
    country: "",
    organization: "",
    project_title: "",
    animals_helped: "",
    amount_requested: "",
    project_description: "",
    timeline: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const next = () => {
    if (step === 0) {
      if (!form.applicant_name || !form.email || !form.country) {
        toast.error("Please fill applicant name, email, and country");
        return;
      }
    }
    if (step === 1) {
      if (!form.project_title || !form.animals_helped || !form.project_description) {
        toast.error("Please complete project details");
        return;
      }
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!form.amount_requested) {
      toast.error("Please specify an amount");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/grants", {
        ...form,
        amount_requested: parseFloat(form.amount_requested),
      });
      setSubmitted(data);
      toast.success("Application submitted!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="grant-success" className="min-h-[80vh] bg-[#0A0A0C] flex items-center">
        <div className="max-w-3xl mx-auto px-6 text-center py-20">
          <Check className="mx-auto text-[#E85D04]" size={64} />
          <h1 className="mt-8 font-serif-display text-5xl lg:text-6xl">Application received.</h1>
          <p className="mt-6 text-white/70 text-lg leading-relaxed">
            Thank you for trusting us with your work. Our grants committee reviews applications
            quarterly. You'll hear from us within 60 days.
          </p>
          <div className="mt-10 inline-block border border-white/10 px-8 py-6 text-left">
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/40">Reference</div>
            <div className="font-serif-display text-3xl text-[#E85D04] mt-1">
              {submitted.reference}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="grant-page" className="bg-[#0A0A0C] text-white py-20">
      <section className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="text-xs tracking-[0.3em] uppercase text-[#E85D04] mb-6">
          Grant Application
        </div>
        <h1 className="font-serif-display text-5xl lg:text-7xl leading-[0.95] tracking-tight">
          Tell us about <em className="not-italic text-[#E85D04]">your work.</em>
        </h1>
        <p className="mt-6 text-white/70 text-lg max-w-2xl">
          Grants of $500 – $25,000 for animal lovers running rescues, sanctuaries, or community
          programs. All applications reviewed by our grants committee.
        </p>

        {/* Step indicator */}
        <div className="mt-14 flex items-center gap-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div
                className={`h-px flex-1 ${i <= step ? "bg-[#E85D04]" : "bg-white/15"}`}
              />
              <div
                className={`text-[10px] tracking-[0.3em] uppercase ${
                  i === step ? "text-[#E85D04]" : "text-white/40"
                }`}
              >
                {String(i + 1).padStart(2, "0")} {s}
              </div>
            </div>
          ))}
        </div>

        {/* Form */}
        <div className="mt-12 border border-white/10 p-8 lg:p-12 bg-[#121215]">
          {step === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Full Name *" testid="grant-name">
                <Input value={form.applicant_name} onChange={(v) => update("applicant_name", v)} testid="grant-name-input" />
              </Field>
              <Field label="Email *" testid="grant-email">
                <Input type="email" value={form.email} onChange={(v) => update("email", v)} testid="grant-email-input" />
              </Field>
              <Field label="Phone">
                <Input value={form.phone} onChange={(v) => update("phone", v)} testid="grant-phone-input" />
              </Field>
              <Field label="Country *">
                <Input value={form.country} onChange={(v) => update("country", v)} testid="grant-country-input" />
              </Field>
              <Field label="Organization (optional)" className="md:col-span-2">
                <Input value={form.organization} onChange={(v) => update("organization", v)} testid="grant-org-input" />
              </Field>
            </div>
          )}
          {step === 1 && (
            <div className="grid grid-cols-1 gap-6">
              <Field label="Project Title *">
                <Input value={form.project_title} onChange={(v) => update("project_title", v)} testid="grant-title-input" />
              </Field>
              <Field label="Animals / Communities You Help *">
                <Input value={form.animals_helped} onChange={(v) => update("animals_helped", v)} testid="grant-animals-input" placeholder="e.g. stray dogs in Bucharest" />
              </Field>
              <Field label="Project Description *">
                <Textarea value={form.project_description} onChange={(v) => update("project_description", v)} rows={6} testid="grant-desc-input" />
              </Field>
              <Field label="Timeline">
                <Input value={form.timeline} onChange={(v) => update("timeline", v)} placeholder="e.g. 6 months" testid="grant-timeline-input" />
              </Field>
            </div>
          )}
          {step === 2 && (
            <div className="grid grid-cols-1 gap-6">
              <Field label="Amount Requested (USD) *">
                <Input type="number" value={form.amount_requested} onChange={(v) => update("amount_requested", v)} testid="grant-amount-input" placeholder="500 – 25000" />
              </Field>
              <div className="text-white/60 text-sm leading-relaxed">
                By submitting, you confirm the information provided is accurate and authorize
                the John Depp Foundation to verify your work.
              </div>
            </div>
          )}

          <div className="mt-10 flex justify-between gap-4">
            <button
              type="button"
              onClick={back}
              disabled={step === 0}
              data-testid="grant-back-btn"
              className="inline-flex items-center gap-2 border border-white/25 hover:border-white text-white px-6 py-4 text-xs tracking-[0.22em] uppercase transition-colors disabled:opacity-20 disabled:cursor-not-allowed disabled:hover:border-white/25"
            >
              ← Back
            </button>
            {step < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={next}
                data-testid="grant-next-btn"
                className="inline-flex items-center gap-3 bg-[#E85D04] hover:bg-[#DC2F02] text-white px-8 py-4 text-xs tracking-[0.22em] uppercase transition-colors"
              >
                Continue <ArrowRight size={14} />
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                data-testid="grant-submit-btn"
                className="inline-flex items-center gap-3 bg-[#E85D04] hover:bg-[#DC2F02] text-white px-8 py-4 text-xs tracking-[0.22em] uppercase disabled:opacity-60"
              >
                {submitting ? "Submitting…" : "Submit Application"}
                <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ============================== GRANT SUCCESS STORIES */}
      <section data-testid="grant-stories-section" className="mt-20 border-t border-white/10 py-24 bg-[#0A0A0C]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-[#E85D04] mb-4">
                Past Recipients
              </div>
              <h2 className="font-serif-display text-5xl lg:text-6xl leading-[0.95] tracking-tight">
                Funded. <em className="not-italic text-[#E85D04]">Delivered.</em>
              </h2>
              <p className="mt-4 text-white/60 max-w-xl">
                These are real applications we funded. Real animals, real communities, real results.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stories.map((s, i) => (
              <article
                key={s.id || i}
                data-testid={`grant-story-${i}`}
                className="group border border-white/10 bg-[#121215] overflow-hidden hover:-translate-y-1 transition-transform"
              >
                <div className="relative aspect-[5/3] overflow-hidden">
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                    style={{ backgroundImage: `url(${s.image_url})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C]/80 to-transparent" />
                  <div className="absolute top-4 left-4 bg-[#E85D04] text-white text-[10px] tracking-[0.25em] uppercase px-3 py-1">
                    {s.category}
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-baseline justify-between gap-4 mb-3">
                    <span className="font-serif-display text-2xl text-[#E85D04]">
                      ${s.amount.toLocaleString()}
                    </span>
                    <span className="text-[10px] tracking-[0.3em] uppercase text-white/40">{s.year}</span>
                  </div>
                  <h3 className="font-serif-display text-xl leading-tight text-white">{s.recipient}</h3>
                  <p className="mt-3 text-white/65 text-sm leading-relaxed">{s.outcome}</p>
                </div>
              </article>
            ))}
            {stories.length === 0 && (
              <div className="col-span-full text-white/40 text-center py-10">
                Loading recipient stories…
              </div>
            )}
          </div>
          <div className="mt-12 text-center text-white/50 text-sm">
            <span className="font-serif-display text-[#E85D04] text-2xl">$1.4M+</span> awarded to <span className="font-serif-display text-[#E85D04] text-2xl">312</span> recipients across <span className="font-serif-display text-[#E85D04] text-2xl">47</span> countries.
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}

function Input({ value, onChange, type = "text", placeholder = "", testid }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full bg-[#0A0A0C] border border-white/10 text-white px-4 py-3 outline-none focus:border-[#E85D04] transition-colors placeholder:text-white/30"
    />
  );
}

function Textarea({ value, onChange, rows = 4, testid }) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      data-testid={testid}
      className="w-full bg-[#0A0A0C] border border-white/10 text-white px-4 py-3 outline-none focus:border-[#E85D04] transition-colors resize-none"
    />
  );
}
